import { Box, Button, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import prebookVideo from './images/launch/prebook-video.mp4';
import posterDark from './images/launch/prebook-poster-dark.jpg';
import posterLight from './images/launch/prebook-poster-light.jpg';
import ti7Cutout from './images/ti7/car-jadegreen.png';
import atto2Cutout from './images/atto2/car-green.png';
import { createWhatsAppUrl } from './siteConfig';

// Sorotan dua model baru yang sudah bisa di-pre-book: BYD Ti7 dan BYD Atto 2.
// variant='full'    : seksi lengkap untuk beranda (video + dua kartu model + poster)
// variant='compact' : versi ringkas untuk halaman model (video + CTA + model lainnya)
// exclude           : slug model yang sedang dibuka ('ti7' | 'atto2') agar tidak
//                     menautkan ke halamannya sendiri

export const PREBOOK_MODELS = [
  {
    slug: 'ti7',
    name: 'BYD Ti7',
    tagline: 'Luxury in every journey',
    desc: 'SUV 7-seater pertama BYD dengan DM-p Super Hybrid AWD, 408 PS, dan jarak listrik murni hingga 119 km.',
    path: '/jelajahi-ti7',
    image: ti7Cutout,
    imageRatio: 50.4,
    accent: '#0f7d7a',
  },
  {
    slug: 'atto2',
    name: 'BYD Atto 2',
    tagline: 'Bigger fun, greener future',
    desc: 'SUV kompak listrik dengan Blade Battery 2.0 dan Flash Charging: 10-97% sekitar 9 menit.',
    path: '/jelajahi-atto2',
    image: atto2Cutout,
    imageRatio: 54.3,
    accent: '#5f7a3a',
  },
];

export const PREBOOK_WA = createWhatsAppUrl(
  'Halo Junius, saya ingin pre-book BYD Ti7 / BYD Atto 2 di BYD Arista BSD City. Mohon info ketentuan tanda jadi, pilihan warna, dan estimasi pengiriman.',
);

function PreBookVideo({ compact = false }) {
  return (
    <Box
      sx={{
        position: 'relative',
        mx: 'auto',
        width: '100%',
        maxWidth: compact ? 300 : { xs: 300, md: 340 },
        borderRadius: '28px',
        p: '10px',
        backgroundColor: '#0b0f14',
        border: '1px solid rgba(255,255,255,0.14)',
        boxShadow: '0 40px 80px rgba(0,0,0,0.55), inset 0 0 0 1px rgba(255,255,255,0.04)',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          borderRadius: '20px',
          overflow: 'hidden',
          backgroundColor: '#000',
          '&::before': { content: '""', display: 'block', paddingTop: '177.8%' },
        }}
      >
        <Box
          component='video'
          src={prebookVideo}
          poster={posterDark}
          autoPlay
          muted
          loop
          playsInline
          preload='metadata'
          aria-label='Video open pre-book BYD Ti7 dan BYD Atto 2 di BYD BSD Serpong'
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
          }}
        />
      </Box>
      <Typography
        sx={{
          position: 'absolute',
          top: 22,
          left: 22,
          px: 1.2,
          py: 0.4,
          borderRadius: '999px',
          backgroundColor: 'rgba(212,175,55,0.92)',
          color: '#1a1400',
          fontSize: '0.68rem',
          fontWeight: 900,
          letterSpacing: 1,
          textTransform: 'uppercase',
        }}
      >
        Open Pre-Book
      </Typography>
    </Box>
  );
}

function ModelCard({ model }) {
  return (
    <Box
      component={RouterLink}
      to={model.path}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        textDecoration: 'none',
        color: '#fff',
        borderRadius: '18px',
        overflow: 'hidden',
        border: '1px solid rgba(255,255,255,0.12)',
        background:
          'linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)',
        transition: 'transform 0.25s ease, border-color 0.25s ease',
        '&:hover': { transform: 'translateY(-4px)', borderColor: 'rgba(212,175,55,0.6)' },
        '&:hover img': { transform: 'scale(1.04)' },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          px: 2,
          pt: 2,
          background: `radial-gradient(ellipse at 50% 85%, ${model.accent}55 0%, rgba(0,0,0,0) 65%)`,
          '&::before': { content: '""', display: 'block', paddingTop: `${model.imageRatio}%` },
        }}
      >
        <Box
          component='img'
          src={model.image}
          alt={`${model.name} open pre-book di BYD BSD Serpong`}
          loading='lazy'
          sx={{
            position: 'absolute',
            top: 10,
            right: 16,
            bottom: 0,
            left: 16,
            width: 'calc(100% - 32px)',
            height: 'calc(100% - 10px)',
            objectFit: 'contain',
            objectPosition: 'center bottom',
            transition: 'transform 0.4s ease',
            filter: 'drop-shadow(0 18px 18px rgba(0,0,0,0.45))',
          }}
        />
      </Box>
      <Box sx={{ p: 2.2, pt: 1.5 }}>
        <Typography
          sx={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: 1, color: '#d4af37', textTransform: 'uppercase' }}
        >
          {model.tagline}
        </Typography>
        <Typography sx={{ mt: 0.3, fontSize: '1.35rem', fontWeight: 900 }}>{model.name}</Typography>
        <Typography sx={{ mt: 0.6, fontSize: '0.92rem', lineHeight: 1.55, color: 'rgba(255,255,255,0.78)' }}>
          {model.desc}
        </Typography>
        <Typography
          sx={{
            mt: 1.4,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            fontSize: '0.85rem',
            fontWeight: 800,
            color: '#fff',
          }}
        >
          Lihat detail <ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />
        </Typography>
      </Box>
    </Box>
  );
}

function PreBookSpotlight({ variant = 'full', exclude }) {
  const models = PREBOOK_MODELS.filter((m) => m.slug !== exclude);
  const compact = variant === 'compact';

  return (
    <Box
      component='section'
      id='pre-book'
      aria-labelledby='pre-book-title'
      sx={{
        position: 'relative',
        overflow: 'hidden',
        px: { xs: 2, md: 6 },
        py: { xs: 6, md: compact ? 7 : 9 },
        color: '#fff',
        backgroundColor: '#0a1420',
        backgroundImage:
          'radial-gradient(circle at 12% 18%, rgba(212,175,55,0.16) 0%, rgba(10,20,32,0) 42%), radial-gradient(circle at 88% 82%, rgba(15,125,122,0.3) 0%, rgba(10,20,32,0) 45%)',
        scrollMarginTop: 96,
      }}
    >
      <Box
        sx={{
          maxWidth: 1240,
          mx: 'auto',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: compact ? '300px 1fr' : '340px 1fr' },
          gap: { xs: 4, md: 6 },
          alignItems: 'center',
        }}
      >
        <PreBookVideo compact={compact} />

        <Box>
          <Typography
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 0.6,
              borderRadius: '999px',
              border: '1px solid rgba(212,175,55,0.6)',
              backgroundColor: 'rgba(212,175,55,0.14)',
              color: '#f1d87a',
              fontSize: '0.76rem',
              fontWeight: 900,
              letterSpacing: 1.2,
              textTransform: 'uppercase',
            }}
          >
            Open Pre-Book · BYD Arista BSD City
          </Typography>
          <Typography
            id='pre-book-title'
            component='h2'
            sx={{
              mt: 1.5,
              fontSize: { xs: '2rem', md: compact ? '2.4rem' : '3.2rem' },
              fontWeight: 900,
              lineHeight: 1.02,
            }}
          >
            {compact
              ? `Pre-book juga tersedia untuk ${models.map((m) => m.name).join(' & ')}`
              : 'Two new exciting models are coming: BYD Ti7 & BYD Atto 2'}
          </Typography>
          <Typography sx={{ mt: 1.5, maxWidth: 680, lineHeight: 1.7, color: 'rgba(255,255,255,0.82)' }}>
            Pre-book resmi dibuka di BYD Arista BSD City. Amankan unit lebih awal,
            dapatkan penawaran eksklusif untuk pemesan pertama, dan jadi yang pertama
            tahu saat harga resmi Indonesia diumumkan. Hubungi Junius untuk
            ketentuan tanda jadi dan estimasi pengiriman.
          </Typography>

          <Box sx={{ mt: 2.5, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Button
              href={PREBOOK_WA}
              target='_blank'
              rel='noopener noreferrer'
              variant='contained'
              startIcon={<WhatsAppIcon />}
              sx={{
                px: 3,
                py: 1.2,
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 800,
                backgroundColor: '#d4af37',
                color: '#1a1400',
                '&:hover': { backgroundColor: '#e3c35a' },
              }}
            >
              Pre-book sekarang via WhatsApp
            </Button>
            {!compact && (
              <Button
                component={RouterLink}
                to='/jelajahi-ti7'
                variant='outlined'
                sx={{
                  px: 3,
                  py: 1.2,
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: 800,
                  color: '#fff',
                  borderColor: 'rgba(255,255,255,0.45)',
                  '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.08)' },
                }}
              >
                Jelajahi BYD Ti7
              </Button>
            )}
          </Box>

          <Box
            sx={{
              mt: 3.5,
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: models.length > 1 ? '1fr 1fr' : 'minmax(0, 420px)',
              },
              gap: 2,
            }}
          >
            {models.map((model) => (
              <ModelCard key={model.slug} model={model} />
            ))}
          </Box>

          {!compact && (
            <Box
              sx={{
                mt: 3,
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 150px))',
                gap: 1.5,
                alignItems: 'start',
              }}
            >
              {[posterDark, posterLight].map((src, i) => (
                <Box
                  key={src}
                  component='img'
                  src={src}
                  alt={i === 0 ? 'Poster open pre-book BYD Ti7 dan Atto 2, hubungi Junius 0812 8686 4825' : 'Poster open pre-book BYD Atto 2 dan BYD Ti7 di BYD Arista'}
                  loading='lazy'
                  sx={{
                    width: '100%',
                    height: 'auto',
                    display: 'block',
                    borderRadius: '10px',
                    border: '1px solid rgba(255,255,255,0.14)',
                  }}
                />
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default PreBookSpotlight;
